import { createFileRoute } from "@tanstack/react-router";
import { Card, CardContent } from "@/components/ui/card";
import { cn, formatNumber } from "@/lib/utils";
import { BarGraph, LineGraph } from "@/components/ui/shared/graphs";
import { useEffect } from "react";
import useAppStore from "@/store";
import SimpleTable from "@/components/ui/table";
import { TopProductTableColums } from "@/components/product/product-meta";
import { TopCategoriesTableColums } from "@/components/category/category-meta";
import { useTranslate } from "@/hooks/use-translate";
import { useStats } from "@/hooks/use-stats";
import type { FinanceAccount } from "@/store/FinanceAccount";
import {
  BanknoteArrowUp,
  BookCheckIcon,
  DollarSign,
  Package,
  TrendingUp,
} from "lucide-react";
import StatisticsForm from "@/components/ui/shared/statistics-form";
import { TopServiceTableColums } from "@/components/service/service-meta";

export const Route = createFileRoute("/(dasboard)/dashboard")({
  component: DashboardPage,
});

export const template = [
  {
    label: "Products sold",
    key: "total_sold",
    value: 0,
    surfix: "",
    color: "text-blue-600",
    bg: "bg-blue-100/70",
    icon: Package,
  },
  {
    label: "Services sold",
    key: "total_services_sold",
    value: 0,
    surfix: "",
    color: "text-orange-600",
    bg: "bg-orange-100/70",
    icon: BookCheckIcon,
  },
  {
    label: "Total Revenue",
    key: "total_incomes",
    value: 0,
    surfix: "XAF",
    color: "text-green-600",
    bg: "bg-green-100/70",
    icon: DollarSign,
  },
  {
    label: "Total expenses",
    key: "total_expenses",
    value: 0,
    surfix: "XAF",
    color: "text-red-600",
    bg: "bg-red-100/70",
    icon: BanknoteArrowUp,
  },
  {
    label: "Net Profit",
    key: "net_profit",
    value: 0,
    surfix: "XAF",
    color: "text-green-600",
    bg: "bg-green-100/70",
    icon: TrendingUp,
  },
];

function DashboardPage() {
  const store = useAppStore();

  const { form, stats, setOverview } = useStats({
    template,
  });

  const { t } = useTranslate();

  const balance = store.financeTransaction.stats.accounts.reduce(
    (sum: number, item: FinanceAccount) => sum + item.balance,
    0,
  );
  const totalDebt = store.financeDebt.stats.total_debt;

  const getSaleSerie = (id: string) => {
    return store.sale.stats.series.datasets.filter(
      (item: any) => item.id == id,
    );
  };

  const getBatchSerie = (id: string) => {
    return store.stockBatch.stats.series.datasets.filter(
      (item: any) => item.id == id,
    );
  };

  useEffect(() => {
    if (store.auth.session) {
      fetchStats();
    }
  }, [store.auth.session]);

  function fetchStats(): void {
    store.financeTransaction
      .getStats(form.values.start_date, form.values.end_date)
      .then((data) => {
        setOverview(data, ["total_expenses", "total_incomes", "net_profit"]);
      });

    store.product
      .getStats(form.values.start_date, form.values.end_date)
      .then((data) => {
        setOverview(data, ["total_sold"]);
      });
    store.service
      .getStats(form.values.start_date, form.values.end_date)
      .then((data) => {
        setOverview(data, ["total_services_sold"]);
      });

    store.sale.getStats(form.values.start_date, form.values.end_date);

    store.stockBatch.getStats(form.values.start_date, form.values.end_date);

    store.financeTransaction.getStats(
      form.values.start_date,
      form.values.end_date,
    );

    store.financeDebt.getStats(form.values.start_date, form.values.end_date);
  }

  return (
    <div className=" max-w-6xl mx-auto">
      <StatisticsForm form={form} fetchStats={fetchStats} />

      <div className="mt-4 grid grid-cols-1 gap-6 md:grid-cols-1 lg:grid-cols-5">
        {stats.map((stat: any, index: number) => (
          <Card
            key={index}
            className={cn(
              "rounded-2xl bg-white transition hover:shadow",
              // stat.bg
            )}
          >
            <CardContent className="flex items-center gap-4 p-4">
              <div className={cn("rounded-md bg-white p-3", stat.bg)}>
                <stat.icon className={cn("h-6 w-6", stat.color)} />
              </div>
              <div>
                <p className="">{t(stat.label)}</p>
                <div className="flex gap-1 font-semibold">
                  <span>{formatNumber(stat.value)}</span>
                  <div>
                    <span className="text-sm text-gray-600">{stat.surfix}</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      <section className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-8 w-full">
        <div className="col-span-2 space-y-6">
          <LineGraph
            title={t("Supply and Sales")}
            colors={[
              ...getBatchSerie("supply_cost"),
              ...getSaleSerie("volume"),
            ].map((item: any) => item.color)}
            categories={store.sale.stats.series.labels}
            series={[
              ...getBatchSerie("supply_cost"),
              ...getSaleSerie("volume"),
            ].map((item: any) => ({
              name: t(item.name),
              data: item.data,
            }))}
          />

          <aside className="flex flex-col col-span-2 bg-white shadow-md p-4 rounded-md">
            <h2 className="font-semibold text-lg">{t("Top products")}</h2>
            <div className="mt-4">
              <SimpleTable
                items={
                  store.product.stats.top_products?.filter(
                    (_: any, i: number) => i < 5,
                  ) || []
                }
                columns={TopProductTableColums({ store })}
              />
            </div>
          </aside>

          <aside className="flex flex-col col-span-2 bg-white shadow-md p-4 rounded-md">
            <h2 className="font-semibold text-lg">{t("Top services")}</h2>
            <div className="mt-4">
              <SimpleTable
                items={
                  store.service.stats.top_services?.filter(
                    (_: any, i: number) => i < 5,
                  ) || []
                }
                columns={TopServiceTableColums({ store })}
              />
            </div>
          </aside>

          <aside className="col-span-2">
            <LineGraph
              title={t("Incomes and expenses")}
              colors={store.financeTransaction.stats.series.datasets.map(
                (item: any) => item.color,
              )}
              categories={store.financeTransaction.stats.series.labels}
              series={store.financeTransaction.stats.series.datasets.map(
                (item: any) => ({
                  name: t(item.name),
                  data: item.data,
                }),
              )}
            />
          </aside>
          <aside className="col-span-2">
            <LineGraph
              title={t("Debts")}
              colors={store.financeDebt.stats.series.datasets.map(
                (item: any) => item.color,
              )}
              categories={store.financeDebt.stats.series.labels}
              series={store.financeDebt.stats.series.datasets.map(
                (item: any) => ({
                  name: t(item.name),
                  data: item.data,
                }),
              )}
            />
          </aside>
        </div>

        <aside className="space-y-6">
          <div className="bg-white rounded-md border border-gray-200 p-4">
            <div
              className={cn("rounded-md p-4", {
                "bg-red-50": balance < 0,
                "bg-green-50": balance > 0,
              })}
            >
              <p className="font-semibold">{t("Total Balance")}</p>
              <p
                className={cn("text-2xl font-semibold", {
                  "text-red-700": balance < 0,
                  "text-green-700": balance > 0,
                })}
              >
                {store.shop.formatAmount(balance)}
              </p>
            </div>
            <div
              className={cn("mt-4 rounded-md p-4", {
                "bg-red-50": totalDebt < 0,
                "bg-green-50": totalDebt > 0,
              })}
            >
              <p className="font-semibold">{t("Gross Debt")}</p>
              <p
                className={cn("text-2xl font-semibold", {
                  "text-red-700": totalDebt < 0,
                  "text-green-700": totalDebt > 0,
                })}
              >
                {store.shop.formatAmount(totalDebt)}
              </p>
            </div>
            <ul className="space-y-4 mt-4">
              {store.financeTransaction.stats.accounts.map((item: any) => (
                <li
                  key={`financeAccount${item.id}`}
                  className={cn("px-4 py-2 rounded-md bg-gray-50")}
                >
                  <div className="font-semibold">{item.name}</div>
                  <div
                    className={cn("font-semibold", {
                      "text-red-800": item.balance < 0,
                      "text-green-800": item.balance > 0,
                    })}
                  >
                    {store.shop.formatAmount(item.balance)}
                  </div>
                </li>
              ))}
            </ul>
          </div>
          <aside className="rounded-md">
            <BarGraph
              title={t("Last 7 days sales")}
              colors={["#155dfc"]}
              categories={
                store.product.stats?.last_7_days_series?.days as string[]
              }
              series={[
                {
                  name: t("Sales"),
                  data:
                    store.product.stats?.last_7_days_series?.data.map(
                      (item: any) => item.total_sold,
                    ) || [],
                },
              ]}
            />
          </aside>

          <aside className="flex flex-col bg-white shadow-md p-4 rounded-md">
            <h2 className="font-semibold text-lg">{t("Top categories")}</h2>
            <SimpleTable
              items={
                store.product.stats?.top_categories?.filter(
                  (_: any, i: number) => i < 5,
                ) || []
              }
              columns={TopCategoriesTableColums({ store })}
            />
          </aside>
        </aside>
      </section>
    </div>
  );
}
